import type React from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import map from 'lodash/map';

// Constellation
import { Text, Tooltip, TooltipPopper } from '@zillow/constellation';
import { PriceMyRentalSearchModule } from '@zg-rentals/zrm-marketing-components';

import { getCanAccessCookieValue } from '../../utils/cookieUtils';

import FeatureFlagConstants from '../../constants/FeatureFlagConstants';

// Components
import LazyLoad from '../../components/LazyLoad';
import ResponsiveHeading from '../../StyledComponents/ResponsiveText';
import SearchModule from '../../modules/SearchModule';

// Misc
import { emitContinueBelowFoldEvent } from 'app/shared/constants/PriceMyRentalCSEvents';
import * as S from './styles';
import RentalComparisonScreenShotWebp from 'images/rental-comparison.webp';

import type { State } from '../../types/State';

const rentZestimateTooltip = (
  <Tooltip
    body={
      <Text fontType="bodySmall" as="p">
        Currently, Zillow has data on more than 168 million homes, and a Rent Zestimate<sup>®</sup> on 115 million of
        them.
      </Text>
    }
  />
);

const competitionTooltip = (
  <Tooltip
    body={
      <Text fontType="bodySmall" as="p">
        A Rent Zestimate<sup>®</sup> is computed using a formula and is a starting point in determining the monthly
        rental price for a specific property.
      </Text>
    }
  />
);

const BENEFITS_COPY = [
  {
    title: (
      <>
        Start with a free Rent Zestimate<sup>®</sup>
      </>
    ),
    body: (
      <>
        The Rent Zestimate<sup>®</sup> uses millions of public data points — plus local market trends — to give you a
        starting point. <TooltipPopper triggered={rentZestimateTooltip} />
      </>
    ),
  },
  {
    title: 'Research other rental homes in your area',
    body: (
      <>
        Sort, filter and analyze a customized list of advertised rentals in your area. Information from similar
        advertised properties makes it easier to set the right price. <TooltipPopper triggered={competitionTooltip} />
      </>
    ),
  },
  {
    title: 'Stay on top of market trends',
    body: (
      <>
        After you receive your Rent Zestimate<sup>®</sup>, we&#39;ll send you resources to help you learn what renters
        are looking for in your local market.
      </>
    ),
  },
];

const UpsellModule = () => {
  const user = useSelector((state: State) => state.user);

  const canAccessZRMMarketingComponents = useMemo(
    () => getCanAccessCookieValue(FeatureFlagConstants.ZRM_MARKETING_COMPONENTS_ENABLED),
    [],
  );

  const renderBenefit = ({
    title,
    body,
    idx,
  }: {
    title: string | React.ReactNode;
    body: string | React.ReactNode;
    idx: number;
  }) => {
    return (
      <S.BenefitContent key={idx}>
        <Text as="h3" fontType="h4" marginBottom="xs">
          {title}
        </Text>
        <Text as="div" fontType="body" marginBottom="sm">
          {body}
        </Text>
      </S.BenefitContent>
    );
  };

  const belowTheFoldTracking = () => {
    emitContinueBelowFoldEvent(user);
  };

  return (
    <S.UpsellModule>
      <ResponsiveHeading level="2" fontType="h3" marginBottom="xs">
        Have questions about pricing your rental?
      </ResponsiveHeading>
      <Text fontType="h4" marginBottom="lg">
        Our landlord tools and resources have you covered.
      </Text>
      <S.Content>
        <S.SectionA>
          <LazyLoad rootMargin="10px">
            <img
              src={RentalComparisonScreenShotWebp as unknown as string}
              alt="Image of the Zillow Price My Rental Tool"
            />
          </LazyLoad>
        </S.SectionA>
        <S.SectionB>
          <S.Copy>{map(BENEFITS_COPY, (textObj, idx) => renderBenefit({ ...textObj, idx }))}</S.Copy>

          {canAccessZRMMarketingComponents ? (
            <PriceMyRentalSearchModule fontColor="dark" customTracking={belowTheFoldTracking} />
          ) : (
            <SearchModule fontColor="dark" customTracking={belowTheFoldTracking} />
          )}
        </S.SectionB>
      </S.Content>
    </S.UpsellModule>
  );
};

export default UpsellModule;
